import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';

const PricesPage = () => {
  const packages = [
    {
      name: 'Starter',
      price: 499,
      description: 'Complete digital presence package for small businesses ready to dominate their local market',
      includes: 'Includes:',
      features: [
        'Website design up to 4 pages',
        'Lead generation form',
        'Website content up to 8 images',
        'SEO optimization',
        'Cookie use policy',
        'Privacy policy',
        'Contact us page',
        '2 social media platforms',
        'GMB profile setup & optimization',
        '30 social media posts per month',
        'Keyword research',
        'Strategic CTAs',
        'Appointment booking automated system',
        'Professional logo design',
        'Image gallery',
        'Email account setup',
        '30 top local citation listings',
        'Search engine submission'
      ],
      iconBg: 'bg-gradient-to-r from-blue-500 to-blue-600',
      icon: 'ri-rocket-line',
      buttonStyle: 'bg-gradient-to-r from-blue-500 to-blue-600'
    },
    {
      name: 'Starter Local SEO',
      price: 499,
      description: 'Essential local SEO package to get your business found in local search results',
      includes: 'Includes:',
      features: [
        '30 top citation listings',
        'Google My Business profile creation and/or optimization',
        'GMB image optimization',
        'Search engine submission',
        'Social media profile creation and/or optimization',
        '1 month social media content posting',
        '2 platforms',
        '10 Keywords',
        'Competitors analysis',
        '1 email campaign',
        '1 text message campaign',
        'Landing page lead generation',
        'On-page SEO (home page)'
      ],
      iconBg: 'bg-gradient-to-r from-emerald-500 to-teal-600',
      icon: 'ri-map-pin-line',
      buttonStyle: 'bg-gradient-to-r from-emerald-500 to-teal-600'
    },
    {
      name: 'Advanced Local SEO',
      price: 799,
      description: 'Comprehensive local SEO solution with ongoing content and marketing campaigns',
      includes: 'All Starter Local SEO features, plus:',
      features: [
        '5 months of social media content posting',
        'GMB posting (5 months)',
        'GMB reviews management',
        '5 email marketing campaigns (1 per month)',
        '5 text message campaigns (1 per month)',
        'Extended social media management',
        'Ongoing reputation management',
        'Monthly performance tracking'
      ],
      iconBg: 'bg-gradient-to-r from-teal-500 to-cyan-600',
      icon: 'ri-line-chart-line',
      buttonStyle: 'bg-gradient-to-r from-teal-500 to-cyan-600',
      badge: 'POPULAR'
    }
  ];

  const addOns = [
    { name: 'Additional Post', price: '$45' },
    { name: 'Extra Photography Session', price: '$250' },
    { name: 'Video Production (30-60s)', price: '$350' },
    { name: 'Paid Ad Management', price: '$300/month' },
    { name: 'Additional Platform', price: '$150/month' },
    { name: 'Rush Content Creation', price: '$100' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Affordable social media content marketing packages designed for small businesses. No hidden fees, no long-term contracts. Just results-driven content that helps you grow.
            </p>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {packages.map((pkg, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:scale-105 relative"
                >
                  {pkg.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                        {pkg.badge}
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <div className={`w-20 h-20 ${pkg.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <i className={`${pkg.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <p className="text-gray-600 mb-4">{pkg.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-gray-900">${pkg.price}</span>
                      <span className="text-gray-600 ml-2">per month</span>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    <p className="font-semibold text-gray-900 text-lg">{pkg.includes}</p>
                    {pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <i className="ri-checkbox-circle-fill text-cyan-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/consultation"
                    className={`block w-full text-center ${pkg.buttonStyle} text-white font-bold py-4 px-8 rounded-full hover:opacity-90 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer`}
                  >
                    Get Started
                  </Link>
                </div>
              ))}

              {/* Premium Package */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-purple-500 relative transform hover:scale-105 transition-all duration-300">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    BEST VALUE
                  </span>
                </div>
                
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-vip-crown-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Premium</h3>
                  <p className="text-gray-600 mb-4">Complete automation with AI-powered solutions</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-gray-900">$1,299</span>
                    <span className="text-gray-600 ml-2">per month</span>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  <p className="font-semibold text-gray-900 text-lg">Everything in Starter & Growth, plus:</p>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>AI Agent Live Help Desk</strong> - 24/7 customer support</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>AI Agent Virtual Receptionist</strong> - Answers calls professionally</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Automated phone call returns</strong> - Never miss a customer</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>AI-powered appointment scheduling</strong> - Books appointments automatically</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Automated email responses</strong> - Instant customer replies</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Automated appointment reminders</strong> - Reduces no-shows</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Complete calendar management</strong> - All appointments organized</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Mobile calendar sync</strong> - Access on iOS & Android</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Priority 24/7 support</strong> - Immediate assistance</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Dedicated account manager</strong> - Personal business advisor</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-purple-600 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700"><strong>Monthly strategy calls</strong> - Ongoing optimization</span>
                  </div>
                </div>

                <Link
                  to="/consultation"
                  className="block w-full text-center bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-4 px-8 rounded-full hover:opacity-90 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Add-ons Section */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Add-On Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {addOns.map((addon, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-800">{addon.name}</span>
                  <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
                    {addon.price}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guarantee Section */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 shadow-xl">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-full mx-auto mb-6">
                <i className="ri-shield-check-fill text-4xl text-white"></i>
              </div>
              <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
                100% Satisfaction Guarantee
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                We're committed to your success. If you're not completely satisfied with our services in the first 30 days, we'll work with you to make it right or provide a full refund. No questions asked.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <i className="ri-time-line text-4xl text-blue-600 mb-2"></i>
                  <div className="font-bold text-gray-800">No Long-Term Contracts</div>
                  <div className="text-sm text-gray-600">Cancel anytime</div>
                </div>
                <div className="text-center">
                  <i className="ri-money-dollar-circle-line text-4xl text-cyan-600 mb-2"></i>
                  <div className="font-bold text-gray-800">Transparent Pricing</div>
                  <div className="text-sm text-gray-600">No hidden fees</div>
                </div>
                <div className="text-center">
                  <i className="ri-customer-service-2-line text-4xl text-blue-600 mb-2"></i>
                  <div className="font-bold text-gray-800">Dedicated Support</div>
                  <div className="text-sm text-gray-600">Always here to help</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Not Sure Which Plan is Right for You?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Let's chat! Book a free consultation and we'll help you choose the perfect package for your business goals and budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consultation"
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Free Consultation
              </Link>
              <Link
                to="/quote"
                className="bg-cyan-400 text-blue-800 px-8 py-4 rounded-full font-bold text-lg hover:bg-cyan-300 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Request Custom Quote
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricesPage;
